<script>
import Avatar from 'next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormInput from 'dashboard/components-next/input/Input.vue';

export default {
  components: {
    Avatar,
    Spinner,
    NextButton,
    FormInput,
  },
  props: {
    notifications: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isUpdating: {
      type: Boolean,
      default: false,
    },
    onClickNotification: {
      type: Function,
      default: () => {},
    },
    onMarkAllDoneClick: {
      type: Function,
      default: () => {},
    },
    onRefresh: {
      type: Function,
      default: () => {},
    },
    onClickRelatedConversation: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      activeFilter: 'all',
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      notificationMetadata: 'notifications/getMeta',
    }),
    filters() {
      return [
        {
          value: 'all',
          label: this.$t('NOTIFICATIONS_PAGE.FILTERS.ALL'),
          icon: 'i-lucide-list-filter',
        },
        {
          value: 'unread',
          label: this.$t('NOTIFICATIONS_PAGE.FILTERS.UNREAD'),
          icon: 'i-lucide-mail',
        },
        {
          value: 'system_alert',
          label: this.$t('NOTIFICATIONS_PAGE.FILTERS.SYSTEM_ALERTS'),
          icon: 'i-lucide-triangle-alert',
        },
      ];
    },
    filteredNotifications() {
      const query = this.searchQuery.trim().toLocaleLowerCase();

      return this.notifications.filter(notification => {
        const matchesFilter =
          this.activeFilter === 'all' ||
          (this.activeFilter === 'unread' && !notification.read_at) ||
          (this.activeFilter === 'system_alert' &&
            this.isSystemAlert(notification));

        if (!matchesFilter) return false;
        if (!query) return true;

        const relatedConversationIds = Array.from(
          notification.primary_actor?.related_conversations || []
        )
          .map(conversation => `#${conversation.display_id}`)
          .join(' ');
        const searchableContent = [
          this.notificationTitle(notification),
          this.notificationMessage(notification),
          this.notificationTypeLabel(notification),
          this.regionLabel(notification),
          relatedConversationIds,
        ]
          .join(' ')
          .toLocaleLowerCase();

        return searchableContent.includes(query);
      });
    },
    pageUnreadCount() {
      return this.notifications.filter(notification => !notification.read_at)
        .length;
    },
    pageSystemAlertCount() {
      return this.notifications.filter(this.isSystemAlert).length;
    },
    showEmptyResult() {
      return !this.isLoading && this.filteredNotifications.length === 0;
    },
    isFiltered() {
      return this.activeFilter !== 'all' || this.searchQuery.trim();
    },
  },
  methods: {
    dynamicTime,
    isSystemAlert(notification) {
      return notification.notification_type === 'system_alert';
    },
    notificationTitle(notification) {
      if (this.isSystemAlert(notification)) {
        return notification.push_message_title;
      }

      const conversationId = notification.primary_actor?.id;
      return conversationId
        ? `${this.$t('NOTIFICATIONS_PAGE.CONVERSATION')} #${conversationId}`
        : this.$t('NOTIFICATIONS_PAGE.DELETE_TITLE');
    },
    notificationMessage(notification) {
      return this.isSystemAlert(notification)
        ? notification.push_message_body
        : notification.push_message_title;
    },
    notificationTypeLabel(notification) {
      const labels = {
        conversation_creation: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_creation'
        ),
        conversation_assignment: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_assignment'
        ),
        assigned_conversation_new_message: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.assigned_conversation_new_message'
        ),
        participating_conversation_new_message: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.participating_conversation_new_message'
        ),
        conversation_mention: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_mention'
        ),
        conversation_label: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.conversation_label'
        ),
        sla_missed_first_response: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_first_response'
        ),
        sla_missed_next_response: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_next_response'
        ),
        sla_missed_resolution: this.$t(
          'NOTIFICATIONS_PAGE.TYPE_LABEL.sla_missed_resolution'
        ),
        system_alert: this.$t('NOTIFICATIONS_PAGE.TYPE_LABEL.system_alert'),
      };
      return (
        labels[notification.notification_type] || notification.notification_type
      );
    },
    notificationIcon(notification) {
      if (this.isSystemAlert(notification)) return 'i-lucide-triangle-alert';

      const icons = {
        conversation_creation: 'i-lucide-message-circle-plus',
        conversation_assignment: 'i-lucide-user-round-check',
        assigned_conversation_new_message: 'i-lucide-message-square-dot',
        participating_conversation_new_message: 'i-lucide-message-square-dot',
        conversation_mention: 'i-lucide-at-sign',
        conversation_label: 'i-lucide-tag',
        sla_missed_first_response: 'i-lucide-clock-alert',
        sla_missed_next_response: 'i-lucide-clock-alert',
        sla_missed_resolution: 'i-lucide-clock-alert',
      };
      return icons[notification.notification_type] || 'i-lucide-bell';
    },
    severityLabel(notification) {
      const severity = notification.primary_actor?.severity;
      const labels = {
        low: this.$t('NOTIFICATIONS_PAGE.SEVERITY.LOW'),
        medium: this.$t('NOTIFICATIONS_PAGE.SEVERITY.MEDIUM'),
        high: this.$t('NOTIFICATIONS_PAGE.SEVERITY.HIGH'),
        critical: this.$t('NOTIFICATIONS_PAGE.SEVERITY.CRITICAL'),
      };
      return labels[severity] || '';
    },
    severityClass(notification) {
      const severity = notification.primary_actor?.severity;
      if (severity === 'critical') return 'severity-critical';
      if (severity === 'high') return 'severity-high';
      if (severity === 'medium') return 'severity-medium';
      return 'severity-low';
    },
    alertStatusLabel(notification) {
      const status = notification.primary_actor?.status;
      const labels = {
        open: this.$t('NOTIFICATIONS_PAGE.STATUS.OPEN'),
        monitoring: this.$t('NOTIFICATIONS_PAGE.STATUS.MONITORING'),
        resolved: this.$t('NOTIFICATIONS_PAGE.STATUS.RESOLVED'),
        closed: this.$t('NOTIFICATIONS_PAGE.STATUS.CLOSED'),
      };
      return labels[status] || '';
    },
    regionLabel(notification) {
      const region = notification.primary_actor?.region || {};
      const cityState = [region.city, region.state].filter(Boolean).join('/');
      return [region.neighborhood, cityState].filter(Boolean).join(' · ');
    },
    affectedCustomers(notification) {
      return notification.primary_actor?.metrics?.distinct_customers;
    },
    clearFilters() {
      this.activeFilter = 'all';
      this.searchQuery = '';
    },
  },
};
</script>

<template>
  <section class="notification-center">
    <header class="notification-header">
      <div class="min-w-0">
        <div class="mb-2 flex items-center gap-2 text-n-brand">
          <span class="header-icon">
            <span class="i-lucide-bell-ring size-4" />
          </span>
          <span class="text-xs font-semibold uppercase tracking-wide">
            {{ $t('NOTIFICATIONS_PAGE.CENTER_LABEL') }}
          </span>
        </div>
        <h1 class="m-0 text-2xl font-semibold text-n-slate-12">
          {{ $t('NOTIFICATIONS_PAGE.HEADER') }}
        </h1>
        <p class="mb-0 mt-1 text-sm text-n-slate-11">
          {{ $t('NOTIFICATIONS_PAGE.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <NextButton
          :label="$t('NOTIFICATIONS_PAGE.REFRESH')"
          icon="i-lucide-refresh-cw"
          color="slate"
          variant="outline"
          size="sm"
          :is-loading="isLoading"
          @click="onRefresh"
        />
        <NextButton
          v-if="notificationMetadata.unreadCount"
          :label="$t('NOTIFICATIONS_PAGE.MARK_ALL_DONE')"
          icon="i-lucide-check-check"
          color="teal"
          variant="faded"
          size="sm"
          :is-loading="isUpdating"
          @click="onMarkAllDoneClick"
        />
      </div>
    </header>

    <div class="summary-grid">
      <article class="summary-card">
        <span class="summary-icon bg-n-brand/10 text-n-brand">
          <span class="i-lucide-inbox size-4" />
        </span>
        <div class="summary-content">
          <strong>{{ notificationMetadata.count || 0 }}</strong>
          <span>{{ $t('NOTIFICATIONS_PAGE.SUMMARY.TOTAL') }}</span>
        </div>
      </article>
      <article class="summary-card">
        <span class="summary-icon bg-n-amber-4 text-n-amber-11">
          <span class="i-lucide-mail size-4" />
        </span>
        <div class="summary-content">
          <strong>{{ notificationMetadata.unreadCount || 0 }}</strong>
          <span>{{ $t('NOTIFICATIONS_PAGE.SUMMARY.UNREAD') }}</span>
        </div>
      </article>
      <article class="summary-card">
        <span class="summary-icon bg-n-ruby-4 text-n-ruby-11">
          <span class="i-lucide-triangle-alert size-4" />
        </span>
        <div class="summary-content">
          <strong>{{ pageSystemAlertCount }}</strong>
          <span>{{ $t('NOTIFICATIONS_PAGE.SUMMARY.SYSTEM_ALERTS') }}</span>
        </div>
      </article>
      <article class="summary-card">
        <span class="summary-icon bg-n-teal-4 text-n-teal-11">
          <span class="i-lucide-eye size-4" />
        </span>
        <div class="summary-content">
          <strong>{{ notifications.length - pageUnreadCount }}</strong>
          <span>{{ $t('NOTIFICATIONS_PAGE.SUMMARY.READ_ON_PAGE') }}</span>
        </div>
      </article>
    </div>

    <div class="notification-toolbar">
      <div class="search-field">
        <span class="search-field-icon i-lucide-search" aria-hidden="true" />
        <FormInput
          v-model="searchQuery"
          :placeholder="$t('NOTIFICATIONS_PAGE.SEARCH_PLACEHOLDER')"
          size="sm"
        />
        <button
          v-if="searchQuery"
          v-tooltip.top="$t('NOTIFICATIONS_PAGE.CLEAR_SEARCH')"
          type="button"
          class="search-clear-button"
          :aria-label="$t('NOTIFICATIONS_PAGE.CLEAR_SEARCH')"
          @click="searchQuery = ''"
        >
          <span class="i-lucide-x size-4" />
        </button>
      </div>
      <div class="filter-buttons">
        <NextButton
          v-for="filter in filters"
          :key="filter.value"
          :label="filter.label"
          :icon="filter.icon"
          color="slate"
          size="sm"
          :variant="activeFilter === filter.value ? 'faded' : 'ghost'"
          @click="activeFilter = filter.value"
        />
      </div>
    </div>

    <div v-if="!isLoading" class="grid gap-3">
      <article
        v-for="notification in filteredNotifications"
        :key="notification.id"
        class="notification-card"
        :class="{
          'is-unread': !notification.read_at,
          'is-system-alert': isSystemAlert(notification),
        }"
        role="button"
        tabindex="0"
        @click="onClickNotification(notification)"
        @keydown.enter="onClickNotification(notification)"
      >
        <div class="notification-card-icon">
          <Avatar
            v-if="notification.primary_actor?.meta?.assignee"
            :src="notification.primary_actor.meta.assignee.thumbnail"
            :size="36"
            :name="notification.primary_actor.meta.assignee.name"
            rounded-full
          />
          <span v-else :class="notificationIcon(notification)" class="size-4" />
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-start justify-between gap-2">
            <div class="min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <h2 class="notification-title">
                  {{ notificationTitle(notification) }}
                </h2>
                <span v-if="!notification.read_at" class="unread-badge">
                  {{ $t('NOTIFICATIONS_PAGE.UNREAD_BADGE') }}
                </span>
                <span
                  v-if="isSystemAlert(notification)"
                  class="severity-badge"
                  :class="severityClass(notification)"
                >
                  {{ severityLabel(notification) }}
                </span>
                <span v-if="isSystemAlert(notification)" class="status-badge">
                  {{ alertStatusLabel(notification) }}
                </span>
              </div>
              <p class="notification-message">
                {{ notificationMessage(notification) }}
              </p>
            </div>
            <span class="notification-time">
              <span class="i-lucide-clock-3 size-3.5" />
              {{ dynamicTime(notification.last_activity_at) }}
            </span>
          </div>

          <div v-if="isSystemAlert(notification)" class="alert-context-grid">
            <div v-if="regionLabel(notification)" class="context-item">
              <span class="i-lucide-map-pin size-4" />
              <span>{{ regionLabel(notification) }}</span>
            </div>
            <div v-if="affectedCustomers(notification)" class="context-item">
              <span class="i-lucide-users size-4" />
              <span>
                {{ affectedCustomers(notification) }}
                {{ $t('NOTIFICATIONS_PAGE.AFFECTED_CUSTOMERS') }}
              </span>
            </div>
            <div class="context-item">
              <span class="i-lucide-radio size-4" />
              <span>{{ $t('NOTIFICATIONS_PAGE.SYSTEM_ALERT.SOURCE') }}</span>
            </div>
          </div>

          <div class="notification-footer">
            <span class="type-badge">
              {{ notificationTypeLabel(notification) }}
            </span>
            <div class="flex flex-wrap items-center gap-2">
              <NextButton
                v-if="notification.primary_actor?.actions?.details_url"
                :label="
                  notification.primary_actor.actions.label ||
                  $t('NOTIFICATIONS_PAGE.SYSTEM_ALERT.DETAILS')
                "
                icon="i-lucide-external-link"
                trailing-icon
                size="sm"
                color="teal"
                variant="faded"
                @click.stop="onClickNotification(notification)"
              />
              <NextButton
                v-for="conversation in notification.primary_actor
                  ?.related_conversations || []"
                :key="conversation.display_id"
                :label="`#${conversation.display_id}`"
                icon="i-lucide-message-circle"
                size="sm"
                color="slate"
                variant="outline"
                @click.stop="
                  onClickRelatedConversation(
                    notification,
                    conversation.display_id
                  )
                "
              />
            </div>
          </div>
        </div>
      </article>
    </div>

    <div v-if="showEmptyResult" class="empty-state-card">
      <span
        :class="isFiltered ? 'i-lucide-search-x' : 'i-lucide-bell-off'"
        class="size-7 text-n-slate-10"
      />
      <h2>
        {{
          isFiltered
            ? $t('NOTIFICATIONS_PAGE.EMPTY_FILTERED')
            : $t('NOTIFICATIONS_PAGE.LIST.404')
        }}
      </h2>
      <p>
        {{
          isFiltered
            ? $t('NOTIFICATIONS_PAGE.EMPTY_FILTERED_DESCRIPTION')
            : $t('NOTIFICATIONS_PAGE.EMPTY_DESCRIPTION')
        }}
      </p>
      <NextButton
        v-if="isFiltered"
        :label="$t('NOTIFICATIONS_PAGE.CLEAR_FILTERS')"
        color="slate"
        variant="outline"
        size="sm"
        @click="clearFilters"
      />
    </div>

    <div v-if="isLoading" class="notifications-loader">
      <Spinner />
      <span>{{ $t('NOTIFICATIONS_PAGE.LIST.LOADING_MESSAGE') }}</span>
    </div>
  </section>
</template>

<style scoped>
.notification-center {
  @apply mx-auto grid w-full max-w-6xl gap-5 px-4 py-6 sm:px-6 lg:py-8;
}
.notification-header {
  @apply flex flex-col justify-between gap-4 sm:flex-row sm:items-center;
}
.header-icon {
  @apply grid size-7 shrink-0 place-items-center rounded-md bg-n-brand/10;
}
.summary-grid {
  @apply grid gap-3 sm:grid-cols-2 xl:grid-cols-4;
}
.summary-card {
  grid-template-columns: 2.25rem minmax(0, 1fr);
  @apply grid items-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3.5;
}
.summary-content {
  @apply flex min-h-9 min-w-0 flex-col justify-center;
}
.summary-content strong {
  @apply text-xl font-semibold leading-6 text-n-slate-12;
}
.summary-content > span {
  @apply mt-0.5 text-xs leading-4 text-n-slate-10;
}
.summary-icon {
  @apply flex size-9 shrink-0 items-center justify-center rounded-lg leading-none ring-1 ring-inset ring-n-alpha-2;
}
.summary-icon > span {
  @apply block size-4 shrink-0;
}
.notification-toolbar {
  @apply flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-3 lg:flex-row lg:items-center lg:justify-between;
}
.search-field {
  @apply relative min-w-0 flex-1 lg:max-w-xl;
}
.search-field :deep(input) {
  padding-left: 2.5rem !important;
  padding-right: 2.5rem !important;
  @apply bg-n-solid-1;
}
.search-field-icon {
  @apply pointer-events-none absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10;
}
.search-clear-button {
  @apply absolute right-1.5 top-1/2 z-10 grid size-7 -translate-y-1/2 place-items-center rounded-md text-n-slate-10 transition hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand;
}
.filter-buttons {
  @apply flex flex-wrap items-center gap-1 rounded-lg bg-n-alpha-1 p-1;
}
.notification-card {
  @apply relative flex cursor-pointer gap-3 rounded-xl border border-n-weak bg-n-solid-2 p-4 transition hover:border-n-strong hover:bg-n-alpha-1 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand sm:p-5;
}
.notification-card.is-unread {
  @apply border-n-brand/40 bg-n-brand/5;
}
.notification-card.is-unread::before {
  content: '';
  @apply absolute bottom-3 left-0 top-3 w-1 rounded-r-full bg-n-brand;
}
.notification-card.is-system-alert {
  @apply border-n-amber-6/70;
}
.notification-card-icon {
  @apply grid size-9 shrink-0 place-items-center rounded-lg bg-n-slate-3 text-n-slate-11 ring-1 ring-inset ring-n-alpha-2;
}
.is-system-alert .notification-card-icon {
  @apply bg-n-amber-4 text-n-amber-11;
}
.notification-title {
  @apply m-0 text-sm font-semibold text-n-slate-12 sm:text-base;
}
.notification-message {
  @apply mb-0 mt-1 max-w-4xl whitespace-normal text-sm leading-5 text-n-slate-11;
}
.notification-time {
  @apply flex shrink-0 items-center gap-1 text-xs text-n-slate-10;
}
.unread-badge,
.severity-badge,
.status-badge,
.type-badge {
  @apply inline-flex items-center rounded-full px-2 py-0.5 text-xxs font-semibold;
}
.unread-badge {
  @apply bg-n-brand/10 text-n-brand;
}
.severity-critical {
  @apply bg-n-ruby-4 text-n-ruby-11;
}
.severity-high,
.severity-medium {
  @apply bg-n-amber-4 text-n-amber-11;
}
.severity-low {
  @apply bg-n-teal-4 text-n-teal-11;
}
.status-badge,
.type-badge {
  @apply bg-n-slate-3 text-n-slate-11;
}
.alert-context-grid {
  @apply mt-3 flex flex-wrap gap-x-4 gap-y-2 rounded-lg bg-n-alpha-2 p-3;
}
.context-item {
  @apply flex items-center gap-1.5 text-xs text-n-slate-11;
}
.notification-footer {
  @apply mt-3 flex flex-col justify-between gap-3 border-t border-n-weak pt-3 sm:flex-row sm:items-center;
}
.empty-state-card {
  @apply grid min-h-64 place-items-center content-center gap-2 rounded-xl border border-dashed border-n-weak bg-n-solid-2 p-8 text-center;
}
.empty-state-card h2 {
  @apply m-0 text-base font-semibold text-n-slate-12;
}
.empty-state-card p {
  @apply mb-2 max-w-md text-sm text-n-slate-10;
}
.notifications-loader {
  @apply flex min-h-64 items-center justify-center gap-3 rounded-xl border border-n-weak bg-n-solid-2 text-sm text-n-slate-11;
}
@media (max-width: 639px) {
  .filter-buttons {
    @apply grid grid-cols-1;
  }
}
</style>
